// src/comments/CommentsThread.jsx
import React from "react";
import { useSnapshot } from "valtio";
import { commentsStore } from "./commentsState";
import {
  fetchComments,
  postComment,
  subscribeComments,
  setResolved,
  deleteComment,
  uploadAttachment,
  toggleAck,
} from "./commentsApi";
import { showToast } from "../ui/toast";
import { addDecalFromFile, openLeft } from "../tattoo/state";
import { supabase } from "../tattoo/lib/supabaseClient";
import { useAuth } from "../auth/AuthProvider.jsx";
import { isSharedView } from "../tattoo/sharedCommentsApi.js";
import { guidePaintRef } from "../tattoo/GuidePaint";
import { buildBodyContext } from "../tattoo/bodyContext";
import { getViewerId } from "../tattoo/share";

/* small helper to pulse + scroll a layer card */
function flashLayerCard(layerId) {
  const el = document.querySelector(`.layer-card[data-layer-id="${layerId}"]`);
  if (!el) return;
  el.classList.add("flash");
  el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  setTimeout(() => el.classList.remove("flash"), 1200);
}

/* ---------- tiny helpers ---------- */
const isImage = (a) => {
  const type = a?.type || a?.file_type || "";
  const url = a?.url || a?.file_url || "";
  if (/^image\//i.test(type)) return true;
  return /\.(png|jpe?g|gif|webp|avif)$/i.test(url);
};
const getUrl = (a) => a?.file_url || a?.url || "";
const getName = (a, i) => a?.file_name || a?.name || `attachment-${i + 1}`;

/* ---------- compact chip button ---------- */
function ChipButton({ onClick, title, active, disabled, children }) {
  return (
    <button
      type="button"
      className="btn btn-ghost"
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-pressed={active ? "true" : "false"}
      disabled={disabled}
      style={{
        fontSize: 12,
        padding: "4px 8px",
        lineHeight: 1,
        borderRadius: 999,
        border: active ? "1px solid #4ade80" : "1px solid #333",
        background: active ? "rgba(74,222,128,0.12)" : "transparent",
        color: active ? "#b7ffb7" : "inherit",
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ImageLightbox({ url, onClose }) {
  const wrapRef = React.useRef(null);
  const imgRef = React.useRef(null);

  // view state
  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);

  // gesture bookkeeping
  const ptsRef = React.useRef(new Map()); // pointerId -> {x,y}
  const lastPinchRef = React.useRef({ dist: 0, cx: 0, cy: 0 });
  const draggingRef = React.useRef(false);
  const lastPosRef = React.useRef({ x: 0, y: 0 });
  const lastTapRef = React.useRef({ t: 0, x: 0, y: 0 });

  // helpers
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const applyTransform = (s, x, y) => {
    const el = imgRef.current;
    if (!el) return;
    el.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
  };

  const clampToBounds = React.useCallback((s, x, y) => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return { s, x, y };

    const wrapRect = wrap.getBoundingClientRect();
    const vw = wrapRect.width;
    const vh = wrapRect.height;

    const margin = 40;

    if (s <= 1) return { s: 1, x: 0, y: 0 };

    const maxX = vw * 0.5 + margin;
    const maxY = vh * 0.5 + margin;

    return { s, x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  }, []);

  const handleDoubleTap = (clientX, clientY) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const cx = clientX - rect.left - rect.width / 2;
    const cy = clientY - rect.top - rect.height / 2;

    const targetScale = scale < 2 ? 2 : 1;
    let nextS = targetScale;

    let nextX = tx - (cx * (nextS - scale)) / nextS;
    let nextY = ty - (cy * (nextS - scale)) / nextS;

    const bounded = clampToBounds(nextS, nextX, nextY);
    setScale(bounded.s);
    setTx(bounded.x);
    setTy(bounded.y);
    applyTransform(bounded.s, bounded.x, bounded.y);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    const wrap = wrapRef.current;
    if (!wrap) return;

    const rect = wrap.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;

    let nextS = clamp(scale * (1 + delta), 1, 6);
    let nextX = tx - (cx * (nextS - scale)) / nextS;
    let nextY = ty - (cy * (nextS - scale)) / nextS;

    const bounded = clampToBounds(nextS, nextX, nextY);
    setScale(bounded.s);
    setTx(bounded.x);
    setTy(bounded.y);
    applyTransform(bounded.s, bounded.x, bounded.y);
  };

  const addPoint = (e) =>
    ptsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  const updatePoint = (e) => {
    if (ptsRef.current.has(e.pointerId)) {
      ptsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
  };
  const removePoint = (e) => ptsRef.current.delete(e.pointerId);

  const distanceAndCenter = () => {
    const pts = Array.from(ptsRef.current.values());
    if (pts.length < 2) return null;
    const [a, b] = pts;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);

    const wrap = wrapRef.current;
    const rect = wrap.getBoundingClientRect();
    const cx = (a.x + b.x) * 0.5 - rect.left - rect.width / 2;
    const cy = (a.y + b.y) * 0.5 - rect.top - rect.height / 2;

    return { dist, cx, cy };
  };

  const onPointerDown = (e) => {
    if (e.target === wrapRef.current && scale === 1) {
      const now = performance.now();
      const dt = now - lastTapRef.current.t;
      const dx = Math.abs(e.clientX - lastTapRef.current.x);
      const dy = Math.abs(e.clientY - lastTapRef.current.y);
      lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
      if (dt < 280 && dx < 12 && dy < 12) {
        onClose?.();
        return;
      }
    }

    wrapRef.current.setPointerCapture?.(e.pointerId);
    addPoint(e);

    const now = performance.now();
    const dt = now - lastTapRef.current.t;
    const dx = Math.abs(e.clientX - lastTapRef.current.x);
    const dy = Math.abs(e.clientY - lastTapRef.current.y);
    if (dt < 280 && dx < 12 && dy < 12) {
      handleDoubleTap(e.clientX, e.clientY);
      lastTapRef.current = { t: 0, x: 0, y: 0 };
      return;
    }
    lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };

    if (ptsRef.current.size === 1) {
      draggingRef.current = true;
      lastPosRef.current = { x: e.clientX, y: e.clientY };
    } else if (ptsRef.current.size === 2) {
      const info = distanceAndCenter();
      if (info) {
        lastPinchRef.current = info;
      }
    }
  };

  const onPointerMove = (e) => {
    if (!ptsRef.current.has(e.pointerId)) return;
    e.preventDefault();
    updatePoint(e);

    if (ptsRef.current.size >= 2) {
      const info = distanceAndCenter();
      if (!info) return;
      const { dist, cx, cy } = info;
      const prev = lastPinchRef.current;
      if (!prev.dist) {
        lastPinchRef.current = info;
        return;
      }

      let nextS = clamp(scale * (dist / prev.dist), 1, 6);
      let nextX = tx - (cx * (nextS - scale)) / nextS;
      let nextY = ty - (cy * (nextS - scale)) / nextS;

      const bounded = clampToBounds(nextS, nextX, nextY);
      setScale(bounded.s);
      setTx(bounded.x);
      setTy(bounded.y);
      applyTransform(bounded.s, bounded.x, bounded.y);

      lastPinchRef.current = { dist, cx, cy };
      draggingRef.current = false;
    } else if (draggingRef.current && scale > 1) {
      const dx = e.clientX - lastPosRef.current.x;
      const dy = e.clientY - lastPosRef.current.y;

      let nextX = tx + dx;
      let nextY = ty + dy;

      const bounded = clampToBounds(scale, nextX, nextY);
      setTx(bounded.x);
      setTy(bounded.y);
      applyTransform(scale, bounded.x, bounded.y);

      lastPosRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const endPointer = (e) => {
    removePoint(e);
    if (ptsRef.current.size < 2) {
      lastPinchRef.current = { dist: 0, cx: 0, cy: 0 };
    }
    if (ptsRef.current.size === 0) {
      draggingRef.current = false;
    }
  };

  React.useEffect(() => {
    const onKey = (e) => (e.key === "Escape" ? onClose?.() : null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  React.useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    applyTransform(1, 0, 0);
  }, [url]);

  return (
    <div
      ref={wrapRef}
      onClick={(e) => {
        if (e.target === wrapRef.current) onClose?.();
      }}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.88)",
        display: "grid",
        placeItems: "center",
        zIndex: 9999,
        padding: 16,
        touchAction: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <img
        ref={imgRef}
        src={url}
        alt=""
        draggable={false}
        style={{
          maxWidth: "96vw",
          maxHeight: "92vh",
          objectFit: "contain",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          transform: "translate(0px, 0px) scale(1)",
          transition: "transform 0.04s linear",
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="btn"
        style={{
          position: "fixed",
          right: 14,
          top: 14,
          padding: "8px 12px",
          fontSize: 14,
        }}
      >
        Close ✕
      </button>
    </div>
  );
}

export default function CommentsThread({ layerId }) {
  const { session } = useAuth(); // logged-in app users
  const sharedGuest = isSharedView(); // any /view link, regardless of auth

  const [text, setText] = React.useState("");
  const [guestName, setGuestName] = React.useState("");
  const [draftFiles, setDraftFiles] = React.useState([]); // [{url,name,size,type,_source?}]
  const [busy, setBusy] = React.useState(false);
  const [lightboxUrl, setLightboxUrl] = React.useState(null);

  const snap = useSnapshot(commentsStore);
  const listRef = React.useRef(null);

  function patchComment(id, patch) {
    const prev = commentsStore.threads?.[layerId] || [];
    const next = prev.map((c) => (c.id === id ? { ...c, ...patch } : c));

    // trigger valtio update
    commentsStore.threads = {
      ...(commentsStore.threads || {}),
      [layerId]: next,
    };
  }

  // Stable display name for logged-in users
  const displayNameRef = React.useRef("Guest");
  const [, forceTick] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (!alive) return;
        const user = data?.user;
        const name =
          user?.user_metadata?.display_name ||
          user?.email?.split?.("@")?.[0] ||
          "Guest";
        displayNameRef.current = name;
        forceTick((t) => t + 1);
      } catch {
        // keep "Guest"
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Unified display name (user or guest)
  const effectiveName = session
    ? displayNameRef.current
    : guestName.trim() || "Guest";

  // Typing presence state (per thread)
  const [typingUsers, setTypingUsers] = React.useState({});
  const typingChannelRef = React.useRef(null);
  const lastTypingSentRef = React.useRef(0);

  // skin tone picker (shared view)
  const [skinTone, setSkinTone] = React.useState("#d19771");

  // initialize from current tattoo state (if available)
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const mod = await import("../tattoo/state");
        const current = mod?.tattoo?.skinColor;
        if (alive && typeof current === "string" && current.startsWith("#")) {
          setSkinTone(current);
        }
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Stable key for realtime presence
  const userKey = React.useMemo(() => {
    const uid = session?.user?.id;
    if (uid) return `user:${uid}`;
    return `guest:${effectiveName}`;
  }, [session, effectiveName]);

  // ✅ model-sketch helpers (MUST be top-level, not inside effects)
  const hasModelSketch = React.useMemo(() => {
    return (draftFiles || []).some((f) => f?._source === "model-sketch");
  }, [draftFiles]);

  const deleteModelSketch = React.useCallback(() => {
    setDraftFiles((prev) =>
      (prev || []).filter((f) => f?._source !== "model-sketch")
    );
    showToast("Sketch removed");
  }, []);

  const removeDraftAtIndex = React.useCallback((idx) => {
    setDraftFiles((prev) => (prev || []).filter((_, i) => i !== idx));
  }, []);

  // Broadcast "sketch ready" state for your UI nudges
  React.useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("tt:sketch-ready", {
          detail: { ready: hasModelSketch },
        })
      );
    } catch {}
  }, [hasModelSketch]);

  // ✅ body-context helpers (MUST be top-level inside component)
  const hasBodyContext = React.useMemo(() => {
    return (draftFiles || []).some((f) => f?._source === "body-context");
  }, [draftFiles]);

  const deleteBodyContext = React.useCallback(() => {
    setDraftFiles((prev) =>
      (prev || []).filter((f) => f?._source !== "body-context")
    );
    showToast("Body context removed");
  }, []);

  const singletons = React.useMemo(
    () => new Set(["body-context", "model-sketch"]),
    []
  );

  // Initial load + realtime comments (with toast)
  React.useEffect(() => {
    if (!layerId) return;

    let cancelled = false;

    (async () => {
      try {
        await fetchComments(layerId);
      } catch (e) {
        console.warn("[comments] fetch failed:", e);
      }
    })();

    const myUserId = session?.user?.id || null;

    const unsub = subscribeComments(layerId, {
      onInsert: (row) => {
        if (cancelled) return;

        // Don't toast our own comment
        if (myUserId && row.author_id === myUserId) return;

        showToast("New comment on this layer 💬");
      },
    });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [layerId, session]);

  // Typing presence channel (Supabase realtime presence)
  React.useEffect(() => {
    if (!layerId) return;

    const channelName = `comments_typing:${layerId}`;
    const channel = supabase
      .channel(channelName, {
        config: { presence: { key: userKey } },
      })
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const next = {};
        Object.entries(state).forEach(([key, entries]) => {
          if (!Array.isArray(entries) || !entries.length) return;
          const meta = entries[0];
          next[key] = {
            name: meta.name || key,
            typing: !!meta.typing,
            lastSeen: meta.lastSeen || Date.now(),
          };
        });
        setTypingUsers(next);
      })
      .subscribe((status) => {
        console.log("[comments] typing channel status:", layerId, status);
      });

    typingChannelRef.current = channel;

    // initial presence as "idle"
    try {
      channel.track({
        name: effectiveName,
        typing: false,
        lastSeen: Date.now(),
      });
    } catch (e) {
      console.warn("[comments] initial presence track failed:", e);
    }

    return () => {
      typingChannelRef.current = null;
      try {
        supabase.removeChannel(channel);
      } catch {}
      setTypingUsers({});
    };
  }, [layerId, userKey, effectiveName]);

  const notifyTyping = React.useCallback(
    (value) => {
      const ch = typingChannelRef.current;
      if (!ch) return;
      const now = Date.now();
      if (now - lastTypingSentRef.current < 800) return; // throttle
      lastTypingSentRef.current = now;

      try {
        ch.track({
          name: effectiveName,
          typing: !!value,
          lastSeen: now,
        });
      } catch (e) {
        console.warn("[comments] track typing failed:", e);
      }
    },
    [effectiveName]
  );

  // Scroll to bottom when thread changes
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight + 100;
  }, [snap.threads[layerId]?.length, layerId]);

  // Focus/scroll when called from the "Comments" button
  React.useEffect(() => {
    const h = (e) => {
      const want = e.detail?.layerId;
      if (!want || want !== layerId) return;
      setTimeout(() => {
        listRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight + 100;
      }, 120);
    };
    window.addEventListener("tt:focus-comments", h);
    return () => window.removeEventListener("tt:focus-comments", h);
  }, [layerId]);

  async function attachFileViaUpload(file, src = null) {
    try {
      const meta = await uploadAttachment(file);
      const tagged = src ? { ...meta, _source: src } : meta;

      setDraftFiles((prev) => {
        const p = prev || [];
        const filtered = singletons.has(src)
          ? p.filter((f) => f?._source !== src)
          : p;
        return [...filtered, tagged];
      });

      showToast(
        src === "model-sketch"
          ? "Sketch attached"
          : src === "body-context"
          ? "Body context attached"
          : "Attachment added"
      );
    } catch (err) {
      console.warn("[comments] upload failed:", err);
      showToast("Upload failed");
    }
  }

  async function handleAttachFromModel() {
    const gp = guidePaintRef.current;
    if (!gp || gp.isEmpty?.()) {
      showToast("Nothing drawn yet on the model");
      return;
    }

    const canvas = gp.getCanvas?.();
    if (!canvas) {
      showToast("Sketch canvas not available");
      return;
    }

    canvas.toBlob(
      async (blob) => {
        try {
          if (!blob) {
            showToast("Could not capture sketch");
            return;
          }
          const file = new File([blob], "sketch-from-model.png", {
            type: "image/png",
          });
          await attachFileViaUpload(file, "model-sketch");
        } catch (err) {
          console.warn("[guest-sketch] attach failed:", err);
          showToast("Could not attach sketch");
        }
      },
      "image/png",
      0.92
    );
  }

  async function handleAttachBodyContext() {
    try {
      const payload = buildBodyContext();
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });

      const file = new File([blob], "body-context.json", {
        type: "application/json",
      });

      await attachFileViaUpload(file, "body-context");
    } catch (e) {
      console.warn("[body-context] attach failed:", e);
      showToast("Could not attach body context");
    }
  }

  // Allow external UIs (paint toolbar, mobile dock) to trigger attach
  React.useEffect(() => {
    if (!layerId) return;
    const handler = () => handleAttachFromModel();
    window.addEventListener("comments:attach-from-model", handler);
    return () =>
      window.removeEventListener("comments:attach-from-model", handler);
  }, [layerId, handleAttachFromModel]); // handleAttachFromModel closes over current state safely

  async function onSend(e) {
    e?.preventDefault?.();

    const body = (text || "").trim();
    const mentions = extractMentions(body);

    if (!body && (!draftFiles || draftFiles.length === 0)) return;

    setBusy(true);
    try {
      const authorHint = effectiveName;

      await postComment({
        layerId,
        body,
        mentions,
        attachmentsDraft: draftFiles,
        author_username_hint: authorHint,
      });

      setText("");
      setDraftFiles([]);
      showToast("Comment posted ✅");

      // stop typing after send
      try {
        typingChannelRef.current?.track({
          name: effectiveName,
          typing: false,
          lastSeen: Date.now(),
        });
      } catch {}

      setTimeout(() => {
        const list = listRef.current;
        if (list)
          list.scrollTo({
            top: list.scrollHeight + 200,
            behavior: "smooth",
          });
      }, 50);
    } catch (e2) {
      console.warn("[comments] post failed:", e2);
      showToast("Could not post comment");
    } finally {
      setBusy(false);
    }
  }

  async function safeSetResolved(id, resolved) {
    // Support multiple signatures without guessing your commentsApi shape
    try {
      // common: setResolved(id, bool)
      return await setResolved(id, resolved);
    } catch (e1) {
      try {
        // common: setResolved({ id, resolved })
        return await setResolved({ id, resolved });
      } catch (e2) {
        try {
          // common: setResolved({ id, layerId, resolved })
          return await setResolved({ id, layerId, resolved });
        } catch (e3) {
          console.warn(
            "[comments] setResolved failed all signatures:",
            e1,
            e2,
            e3
          );
          throw e3;
        }
      }
    }
  }

  async function onAcknowledge(id) {
    try {
      // one-way: set true only
      await safeSetResolved(id, true);
    } catch (e) {
      showToast("Could not acknowledge");
    }
  }

  async function onDeleteComment(id) {
    const ok = window.confirm("Delete this comment? This cannot be undone.");
    if (!ok) return;

    try {
      await deleteComment({ id, layerId });
      showToast("Comment deleted");
    } catch (e) {
      console.warn("[comments] delete failed:", e);
      showToast("Could not delete comment");
    }
  }

  async function onPickFiles(ev) {
    const files = Array.from(ev.target.files || []);
    ev.target.value = "";
    if (!files.length) return;

    try {
      for (const f of files) {
        await attachFileViaUpload(f);
      }
    } catch {
      // already logged & toasted
    }
  }

  async function onToggleResolved(id, current) {
    try {
      await setResolved(id, !current); // ✅ only this signature
    } catch (e) {
      console.warn("[comments] setResolved failed:", e);
      showToast("Could not update acknowledged status");
    }
  }

  async function handleCreateLayerFromAttachment(a) {
    try {
      openLeft();
      const resp = await fetch(a.url);
      const blob = await resp.blob();
      const file = new File([blob], a.name || "attachment.png", {
        type: blob.type,
      });
      const L = await addDecalFromFile(file, {
        name: a.name?.replace(/\.[^.]+$/, ""),
        initialScale: 1.0,
      });
      if (L?.id) {
        requestAnimationFrame(() => setTimeout(() => flashLayerCard(L.id), 50));
        showToast("Layer created from attachment ✅");
      }
    } catch (err) {
      console.error("[attachment] create layer failed:", err);
      showToast("Could not create layer from attachment");
    }
  }

  const thumbStyle = {
    width: 96,
    height: 96,
    borderRadius: 8,
    border: "1px solid #2b2b2b",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    background: "#0f0f0f",
    cursor: "pointer",
    touchAction: "manipulation",
  };

  const liveThread = snap.threads?.[layerId] ?? [];

  // Build typing indicator label
  const typingLabel = React.useMemo(() => {
    const others = Object.entries(typingUsers)
      .filter(([key, u]) => key !== userKey && u.typing)
      .map(([, u]) => u.name);
    if (!others.length) return null;
    if (others.length === 1) return `${others[0]} is typing…`;
    return `${others.slice(0, 2).join(", ")} are typing…`;
  }, [typingUsers, userKey]);

  return (
    <div className="comments-wrap" style={{ marginTop: 12 }}>
      {lightboxUrl && (
        <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}

      <div
        ref={listRef}
        className="comments-list"
        style={{
          maxHeight: 220,
          width: 300,
          overflow: "auto",
          padding: 8,
          borderRadius: 8,
          border: "1px solid #2b2b2b",
          background: "rgba(20,20,20,0.6)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {liveThread.length === 0 ? (
          <div className="tt-muted" style={{ fontSize: 13, opacity: 0.8 }}>
            No comments yet.
          </div>
        ) : (
          liveThread.map((c) => {
            const myId = session?.user?.id || null; // wherever you keep session
            const isAuthor = !!myId && c.author_id === myId;
            const canAck = !!myId && !isAuthor && !c.resolved;
            const isGuestAuthor = !c.author_id;
            const vid = getViewerId();
            const acked = !!c.ack_by?.[vid];
            const ackCount = c.ack_by ? Object.keys(c.ack_by).length : 0;
            const ackLabel = acked ? `👍✅ ${ackCount}` : `👍 ${ackCount}`;
            console.log("[ACKDBG] render", {
              layerId,
              id: c.id,
              acked,
              vid,
              ack_by: c.ack_by,
            });
            const name =
              c.author_username || c.author_id?.slice?.(0, 6) || "User";

            return (
              <div
                key={c.id}
                className="comment-row"
                style={{
                  display: "grid",
                  gap: 6,
                  padding: "8px 6px",
                  borderBottom: "1px solid #242424",
                }}
              >
                {/* header row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    <strong
                      style={
                        isGuestAuthor ? { fontStyle: "italic" } : undefined
                      }
                    >
                      {name}
                    </strong>{" "}
                    <span>
                      {c.created_at
                        ? new Date(c.created_at).toLocaleString()
                        : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <ChipButton
                      onClick={async () => {
                        const vid = getViewerId();
                        try {
                          const updated = await toggleAck({
                            id: c.id,
                            viewerId: vid,
                          });
                          if (!updated?.ack_by)
                            throw new Error("No ack_by returned");

                          const prev = commentsStore.threads?.[layerId] || [];
                          const next = prev.map((x) =>
                            x.id === c.id ? { ...x, ack_by: updated.ack_by } : x
                          );

                          commentsStore.threads = {
                            ...(commentsStore.threads || {}),
                            [layerId]: next,
                          };
                        } catch (e) {
                          console.warn("[comments] toggleAck failed", e);
                          showToast("Could not update 👍");
                        }
                      }}
                      title={acked ? "Un-acknowledge" : "Acknowledge"}
                      active={acked}
                    >
                      {ackLabel}
                    </ChipButton>

                      

                  </div>
                  {/* Delete (RLS will block if not allowed) */}
                  <button
                    className="btn btn-ghost"
                    onClick={() => onDeleteComment(c.id)}
                    style={{ fontSize: 12 }}
                    title="Delete comment"
                  >
                    🗑
                  </button>
                </div>

                {/* body */}
                {c.body && (
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                    {c.body}
                  </div>
                )}

                {/* attachments */}
                {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {c.attachments.map((a, i) => {
                      const url = getUrl(a);
                      const name = getName(a, i);
                      const img = isImage(a);

                      const isBodyJson =
                        a?._source === "body-context" ||
                        /body-context\.json$/i.test(name || "") ||
                        /\.json(\?|$)/i.test(url || "") ||
                        (a?.file_type || a?.type) === "application/json";

                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          {/* Left: preview (image) or file chip */}
                          {img ? (
                            <div
                              role="button"
                              title={name}
                              style={thumbStyle}
                              onClick={() => setLightboxUrl(url)}
                            >
                              <img
                                src={url}
                                alt={name}
                                loading="lazy"
                                decoding="async"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            </div>
                          ) : (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-ghost"
                              style={{ fontSize: 12 }}
                            >
                              📎 {name}
                            </a>
                          )}

                          {/* Right: actions */}
                          {isBodyJson ? (
                            sharedGuest ? (
                              <a
                                href={url}
                                download={name || "body-context.json"}
                                className="btn btn-ghost"
                                style={{ fontSize: 12 }}
                                title="Download body context JSON"
                              >
                                ⬇️ Download
                              </a>
                            ) : (
                              !!session && (
                                <button
                                  className="btn btn-ghost"
                                  style={{ fontSize: 12 }}
                                  onClick={async () => {
                                    try {
                                      const resp = await fetch(url);
                                      const json = await resp.json();
                                      const { applyBodyContext, openLeft } =
                                        await import("../tattoo/state");
                                      openLeft?.();
                                      const ok = applyBodyContext(json);
                                      showToast(
                                        ok
                                          ? "Applied body context ✅"
                                          : "Could not apply body context"
                                      );
                                    } catch (e) {
                                      console.warn(
                                        "[body-context] apply failed:",
                                        e
                                      );
                                      showToast("Could not apply body context");
                                    }
                                  }}
                                  title="Apply the client's morphs + skin to your model"
                                >
                                  🧍 Apply
                                </button>
                              )
                            )
                          ) : (
                            <button
                              className="btn btn-ghost"
                              style={{ fontSize: 12 }}
                              onClick={() =>
                                handleCreateLayerFromAttachment({ url, name })
                              }
                            >
                              ➕ Layer
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Draft attachments preview (with thumbnails + X) */}
      {draftFiles.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 8,
            fontSize: 12,
            opacity: 0.95,
          }}
        >
          {draftFiles.map((a, i) => {
            const url = getUrl(a) || a.url;
            const name = getName(a, i);
            const isBody = a?._source === "body-context";
            const img = isImage(a);

            // ✅ BODY CONTEXT (JSON) chip with remove
            if (isBody) {
              return (
                <div
                  key={i}
                  title={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px solid #2b2b2b",
                    background: "#0f0f0f",
                  }}
                >
                  <span style={{ fontSize: 12 }}>🧍 Physique</span>

                  <button
                    type="button"
                    onClick={() => deleteBodyContext()}
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "4px 8px" }}
                    title="Remove Physique"
                  >
                    ✕
                  </button>
                </div>
              );
            }

            // ✅ IMAGE THUMB
            if (img) {
              return (
                <div
                  key={i}
                  title={name}
                  style={{ ...thumbStyle, position: "relative" }}
                >
                  <img
                    src={url}
                    alt={name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeDraftAtIndex(i);
                    }}
                    title="Remove attachment"
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      border: "1px solid #444",
                      background: "rgba(0,0,0,0.72)",
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>

                  {a?._source === "model-sketch" && (
                    <div
                      style={{
                        position: "absolute",
                        left: 6,
                        bottom: 6,
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 999,
                        border: "1px solid #333",
                        background: "rgba(0,0,0,0.55)",
                        color: "#eaeaea",
                      }}
                    >
                      sketch
                    </div>
                  )}
                </div>
              );
            }

            // ✅ OTHER FILES (PDF, etc) show as a chip + removable
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #2b2b2b",
                  background: "#0f0f0f",
                }}
                title={name}
              >
                <span style={{ fontSize: 12 }}>📎 {name}</span>
                <button
                  type="button"
                  onClick={() => removeDraftAtIndex(i)}
                  className="btn btn-ghost"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                  title="Remove attachment"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Guest name field (shared view only) */}
      {sharedGuest && (
        <div style={{ marginTop: 8 }}>
          <input
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Your name (optional)"
            maxLength={40}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid #2b2b2b",
              background: "#111",
              color: "#f2f2f2",
              fontSize: 13,
              marginBottom: 6,
            }}
          />

          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (hasModelSketch) deleteModelSketch();
              else handleAttachFromModel();
            }}
            disabled={busy}
            title={
              hasModelSketch
                ? "Remove the attached sketch"
                : "Attach your sketch from the model painter"
            }
            style={{ fontSize: 12 }}
          >
            {hasModelSketch ? "Delete sketch" : "Attach sketch from model"}
          </button>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (hasBodyContext) deleteBodyContext();
              else handleAttachBodyContext();
            }}
            disabled={busy}
            style={{ fontSize: 12 }}
            title={
              hasBodyContext
                ? "Remove attached body context"
                : "Attach your body settings (morphs + skin) with this feedback"
            }
          >
            {hasBodyContext ? "Delete body" : "Attach body"}
          </button>
        </div>
      )}

      {/* Typing indicator */}
      {typingLabel && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            opacity: 0.75,
            fontStyle: "italic",
          }}
        >
          {typingLabel}
        </div>
      )}

      {/* composer */}
      <form
        onSubmit={onSend}
        style={{
          display: "flex",
          gap: 8,
          marginTop: 6,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          value={text}
          onChange={(e) => {
            const v = e.target.value;
            setText(v);
            notifyTyping(v);
          }}
          placeholder={`Comment as ${effectiveName}…  Use @name to mention`}
          style={{
            flex: 1,
            minWidth: 180,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #2b2b2b",
            background: "#141414",
            color: "#f2f2f2",
          }}
        />
        {/* little callout (shared guest only) */}

        <label className="btn btn-ghost" style={{ cursor: "pointer" }}>
          📎
          <input
            type="file"
            accept="image/*,application/pdf"
            multiple
            style={{ display: "none" }}
            onChange={onPickFiles}
          />
        </label>
        {sharedGuest && !hasBodyContext && (
          <div
            style={{
              margin: "6px 0 10px",
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid #2b2b2b",
              background: "#0f0f0f",
              fontSize: 12,
              opacity: 0.9,
              lineHeight: 1.35,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Optional: match your body
            </div>
            <div>
              Adjust your physique &amp; skin tone before leaving feedback — it
              helps the artist fit the design to you.
            </div>
          </div>
        )}
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}

function extractMentions(text = "") {
  const re = /@([a-z0-9._-]{2,40})/gi;
  const out = new Set();
  let m;
  while ((m = re.exec(text))) out.add(m[1].toLowerCase());
  return [...out].slice(0, 10);
}
